import { createModule } from '@evyweb/ioctopus';

import {
  createCouponUseCase,
  deleteCouponUseCase,
  deleteCouponWalletUseCase,
  getCouponsUseCase,
  getCouponUseCase,
  getCouponWalletsUseCase,
  updateCouponBoardUseCase,
  updateCouponUseCase,
  updateCouponWalletUseCase,
} from '../../application/entry.js';
import { couponRepository } from '../../infrastructure/entry.browser.js';
import {
  createCouponController,
  deleteCouponController,
  getCouponController,
  getCouponsController,
  getCouponWalletsController,
  resetCouponController,
  updateCouponBoardController,
  updateCouponController,
  updateCouponWalletController,
} from '../../interface-adapters/entry.browser.js';
import { diSymbolMap } from '../types.js';

/**
 * Creates and configures the coupon module.
 *
 * @returns The configured coupon module.
 */
export function createCouponModule(): ReturnType<typeof createModule> {
  const couponModule = createModule();

  // Repositories
  couponModule
    .bind(diSymbolMap.ICouponRepository)
    .toHigherOrderFunction(couponRepository, [diSymbolMap.ICouponZustandStoreService, diSymbolMap.IDefinitionService]);

  // Use cases
  couponModule
    .bind(diSymbolMap.IUpdateCouponUseCase)
    .toHigherOrderFunction(updateCouponUseCase, [diSymbolMap.ICouponRepository]);
  couponModule
    .bind(diSymbolMap.IUpdateCouponBoardUseCase)
    .toHigherOrderFunction(updateCouponBoardUseCase, [diSymbolMap.ICouponRepository]);
  couponModule
    .bind(diSymbolMap.IGetCouponUseCase)
    .toHigherOrderFunction(getCouponUseCase, [diSymbolMap.ICouponRepository]);
  couponModule
    .bind(diSymbolMap.IGetCouponsUseCase)
    .toHigherOrderFunction(getCouponsUseCase, [diSymbolMap.ICouponRepository]);
  couponModule
    .bind(diSymbolMap.ICreateCouponUseCase)
    .toHigherOrderFunction(createCouponUseCase, [diSymbolMap.ICouponRepository, diSymbolMap.IDefinitionService]);
  couponModule
    .bind(diSymbolMap.IDeleteCouponUseCase)
    .toHigherOrderFunction(deleteCouponUseCase, [diSymbolMap.ICouponRepository]);
  couponModule
    .bind(diSymbolMap.IUpdateCouponWalletUseCase)
    .toHigherOrderFunction(updateCouponWalletUseCase, [diSymbolMap.ICouponRepository]);
  couponModule
    .bind(diSymbolMap.IDeleteCouponWalletUseCase)
    .toHigherOrderFunction(deleteCouponWalletUseCase, [diSymbolMap.ICouponRepository]);
  couponModule
    .bind(diSymbolMap.IGetCouponWalletsUseCase)
    .toHigherOrderFunction(getCouponWalletsUseCase, [diSymbolMap.ICouponRepository]);

  // Controllers
  couponModule
    .bind(diSymbolMap.IUpdateCouponController)
    .toHigherOrderFunction(updateCouponController, [diSymbolMap.IUpdateCouponUseCase]);
  couponModule
    .bind(diSymbolMap.IUpdateCouponBoardController)
    .toHigherOrderFunction(updateCouponBoardController, [diSymbolMap.IUpdateCouponBoardUseCase]);
  couponModule
    .bind(diSymbolMap.IGetCouponController)
    .toHigherOrderFunction(getCouponController, [diSymbolMap.IGetCouponUseCase]);
  couponModule
    .bind(diSymbolMap.IGetCouponsController)
    .toHigherOrderFunction(getCouponsController, [diSymbolMap.IGetCouponsUseCase]);
  couponModule
    .bind(diSymbolMap.ICreateCouponController)
    .toHigherOrderFunction(createCouponController, [diSymbolMap.ICreateCouponUseCase]);
  couponModule
    .bind(diSymbolMap.IDeleteCouponController)
    .toHigherOrderFunction(deleteCouponController, [
      diSymbolMap.IDeleteCouponUseCase,
      diSymbolMap.IDeleteCouponWalletUseCase,
      diSymbolMap.IGetCouponWalletsUseCase,
      diSymbolMap.IUpdateCouponWalletUseCase,
    ]);
  couponModule
    .bind(diSymbolMap.IResetCouponController)
    .toHigherOrderFunction(resetCouponController, [
      diSymbolMap.IGetCouponUseCase,
      diSymbolMap.IUpdateCouponBoardUseCase,
      diSymbolMap.IDefinitionService,
    ]);
  couponModule
    .bind(diSymbolMap.IUpdateCouponWalletController)
    .toHigherOrderFunction(updateCouponWalletController, [diSymbolMap.IUpdateCouponWalletUseCase]);
  couponModule
    .bind(diSymbolMap.IGetCouponWalletsController)
    .toHigherOrderFunction(getCouponWalletsController, [diSymbolMap.IGetCouponWalletsUseCase]);

  return couponModule;
}
